import React from 'react';

import { getIsEVorHFC } from '../../../../library/fitment/fitment-utils';
import Image from '../../../../library/image/image';
import Typography from '../../../../library/typography/typography';
import CmsYoutubeVideo from '../../../../components/cms-youtube-video/cms-youtube-video';
import Message from '../../../../components/message/message';
import TreadwellPersonaIcon from '../../../../components/treadwell-persona-icon/treadwell-persona-icon';

import { GET_CMS_TREADWELL_TAB } from './graphql/treadwell-product-banner-queries';

import {
  useContainerQuery,
  useFitment,
  useMyAccount,
  useQuery,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import { parseCmsContentAndMapComponents } from '../../../../shared/utils/cms/cms';
import { getIsAppBasedDriver } from '../../../../shared/utils/treadwell/treadwell';

import { TREADWELL_CONSUMER_PICKS_MSG } from '../../../../shared/constants/messages';
import { PRODUCT_TYPES } from '../../../../shared/constants/products';

import styles from './treadwell-product-banner.scss';

interface TreadwellProductBannerProps {
  lowestPriceProduct?: { isOnlineOnly: boolean; price: number };
  productCount: number;
  productType: keyof typeof PRODUCT_TYPES;
}

const componentMap = new Map([['youtube-video', CmsYoutubeVideo]]);

function TreadwellProductBanner(props: TreadwellProductBannerProps) {
  const { lowestPriceProduct, productCount, productType } = props;
  const { treadwellPreferences } = useTreadwellPreferences();
  const { isMinLg } = useContainerQuery();
  const { data } = useQuery(GET_CMS_TREADWELL_TAB);
  const { isFleetAccount, user } = useMyAccount();
  const isAppBasedDriver = getIsAppBasedDriver();
  const { selectedFitment } = useFitment();
  const isEVorHFC = getIsEVorHFC(selectedFitment);

  const {
    selectedCustomDrivingPriorities,
    selectedDriverType,
    selectedDrivingPreferences,
    zipCodeWeatherData
  } = treadwellPreferences || {};
  const { icon, name } = selectedDriverType || {};
  const { city, state } = zipCodeWeatherData || {};
  const { downtimeCost, fuelCost, milesDrivenAnnually, vehicleFuelMileage } =
    selectedDrivingPreferences || {};

  const cmsSections = data?.cms?.page?.contentSlots || [];
  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;

  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;

  const driverValue = name || 'Custom';
  const companyDriverValue = user?.companyName;

  if (!treadwellPreferences) {
    return (
      <div className={styles['cms-container']}>
        {cmsSections.map(section =>
          parseCmsContentAndMapComponents(
            section?.cmsComponents?.[0]?.html || '',
            componentMap
          )
        )}
      </div>
    );
  }

  return (
    <div className={styles['container']} data-testid="treadwell-product-banner">
      {isMinLg && (
        <Image
          alt={productType}
          height="257"
          src={
            window.isDT
              ? 'https://discounttire.scene7.com/is/image/discounttire/plp-hero-asset_treadwell-dt?$PLP-hero$'
              : 'https://discounttire.scene7.com/is/image/discounttire/plp-hero-asset_treadwell-at?$PLP-hero$'
          }
          width="278"
        />
      )}
      <div className={styles['description-container']}>
        <Typography className={styles['title']} tag="div" variant="H3">
          <Image
            alt="Treadwell"
            className={styles['treadwell-icon']}
            height="24px"
            src="https://discounttire.scene7.com/is/content/discounttire/logos/treadwell-icon-only-trademark.svg"
          />
          We found&nbsp;
          <strong>
            {productCount} perfect tire matches
            {!lowestPriceProduct?.price && ' for'}
          </strong>
          {lowestPriceProduct?.price && (
            <span>
              {', '}
              starting{lowestPriceProduct?.isOnlineOnly && ' online'} at{' '}
              <strong>${lowestPriceProduct.price}</strong>
            </span>
          )}
        </Typography>

        <Typography tag="div" variant="H6">
          {!lowestPriceProduct?.price && 'a '}
          <strong>
            {name && <TreadwellPersonaIcon iconName={icon} />} {''}
            {isFleetTreadwell ? companyDriverValue : driverValue}{' '}
          </strong>
          {!isAppBasedDriver ? 'driver' : ''} in{' '}
          <strong className={styles['city']}>
            {city?.toLowerCase()}, {state}
          </strong>
        </Typography>
        {isFleetTreadwell || isAppBasedDriver ? (
          <div className={styles['preferences-container']}>
            <div className={styles['driving-preference']}>
              <Typography className={styles['driving-preference-title']}>
                {isEVorHFC ? 'Vehicle Efficiency' : 'Vehicle Fuel Mileage'}
              </Typography>
              <Typography className={styles['driving-preference-value']}>
                {vehicleFuelMileage} {isEVorHFC ? 'mi/kWh' : 'mpg'}
              </Typography>
            </div>
            <div className={styles['driving-preference']}>
              <Typography className={styles['driving-preference-title']}>
                {isEVorHFC ? 'Energy Cost' : 'Fuel Cost'}
              </Typography>
              <Typography className={styles['driving-preference-value']}>
                {isEVorHFC ? '' : '$'}
                {fuelCost}
                {isEVorHFC ? ' cents/kWh' : '/gal'}
              </Typography>
            </div>
            <div className={styles['driving-preference']}>
              <Typography className={styles['driving-preference-title']}>
                Miles Driven Annually
              </Typography>
              <Typography className={styles['driving-preference-value']}>
                {Number(milesDrivenAnnually).toLocaleString('en-US')} miles/yr
              </Typography>
            </div>
            {isFleetTreadwell && (
              <div className={styles['driving-preference']}>
                <Typography className={styles['driving-preference-title']}>
                  Service Downtime Cost
                </Typography>
                <Typography className={styles['driving-preference-value']}>
                  ${downtimeCost}/hour
                </Typography>
              </div>
            )}
          </div>
        ) : (
          <>
            <Typography className={styles['size']} size="extraSmall" tag="div">
              YOUR DRIVING PRIORITIES
            </Typography>
            <div className={styles['priorities-container']}>
              {selectedDriverType
                ? selectedDriverType?.tooltips?.map(
                    (priority, index) =>
                      index < 3 && (
                        <Typography
                          className={styles['driving-priorities']}
                          key={priority.id}
                          tag="div"
                        >
                          {`${index + 1}. ${priority.title}`}
                        </Typography>
                      )
                  )
                : selectedCustomDrivingPriorities?.map(
                    (drivingPriority, index) =>
                      index < 3 && (
                        <Typography
                          className={styles['driving-priorities']}
                          key={drivingPriority.id}
                          tag="div"
                        >
                          {index + 1}. {drivingPriority.name}
                        </Typography>
                      )
                  )}
            </div>
          </>
        )}

        {isFleetEnabled && process.env.__FF_FLEET_TREADWELL__ !== 'true' && (
          <Message
            className={styles['treadwell-consumer-picks-msg']}
            data-testid="treadwell-product-banner__treadwell-consumer-picks-msg"
            message={TREADWELL_CONSUMER_PICKS_MSG}
          />
        )}
      </div>
    </div>
  );
}

export default TreadwellProductBanner;
